import { Box, Link, Stack, Typography } from '@mui/material';
import imgWin from '@/assets/images/win.png';
import imgAlert from '@/assets/images/alert.png';
import iconSuccess from '@/assets/images/success.png';
import { ArrowForwardIos } from '@mui/icons-material';
import React from 'react';
import { CardModel, ColumnModel, ResponsibleCardModel } from '@/features/crm/types';
import DateHelper from '@/utils/DateHelper.ts';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import { ResponsibleModel } from '@/features/responsible/types';
import { SerieModel } from '../list-serie/types';
import { useNavigate } from 'react-router-dom';

type DraggedItemProps = {
  columnId: number;
  itemId: number;
}

type ColumnCardsProps = {
  step: ColumnModel;
  draggedItem: DraggedItemProps | null;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>, targetColumnId: number, targetItemId: number | null) => void;
  handleDragEnd: () => void;
  handleDragStart: (e: React.DragEvent<HTMLDivElement>, columnId: number, itemId: number) => void;
  filterName?: string;
  isUpdating?: boolean;
}

const ICON_STATUS = {
  1: imgWin,
  2: imgAlert,
};

const ColumnCards: React.FC<ColumnCardsProps> = ({
                                                   step,
                                                   draggedItem,
                                                   handleDragOver,
                                                   handleDragEnd,
                                                   handleDragStart,
                                                   handleDrop,
                                                   filterName,
                                                   isUpdating = false
                                                 }) => {
  // Suporta tanto cards paginados quanto array direto
  const cardsData = step?.cards as any;
  const cards = Array.isArray(cardsData) 
    ? cardsData 
    : (cardsData?.results || []);
    
  const { filters, toggleCardResponsible, setResponsible, setCard, columns } = useModalStore((state) => state);
  const columnDone = columns[columns.length - 1];
  const navigate = useNavigate();

  return (
    <>
      {cards
        .filter((card) => {
          // Validação de segurança - ignora cards inválidos
          if (!card || !card.responsavel) return false;
          
          const responsavel = card.responsavel as ResponsibleCardModel;
          const serie = responsavel?.serie || {
            id: null,
            turma: {id: null}, turno: {id: null}
          }
          const turma = serie.turma
          const turno = serie.turno
          const nomeResponsavel = responsavel?.nome || ''
          const nomeAluno = responsavel?.aluno_nome || ''
          const { serieId = 'all', turmaId = 'all', turnoId = 'all'  } = filters

          return (serie.id === serieId || serieId === 'all') &&
            (turma?.id === turmaId || turmaId === 'all') &&
            (turno?.id === turnoId || turnoId === 'all') &&
            (nomeAluno.toLocaleLowerCase().includes(filterName?.toLocaleLowerCase() || ''))
        })
        .map((item: CardModel) => {
        const responsavel = item.responsavel as any
        const serie = responsavel.serie || {
          turma: {}, turno: {}
        } as SerieModel;
        const isDone = columnDone?.id === item.step;

        return (
          <div
            key={item.id}
            draggable={!isUpdating}
            onDragStart={(e) => !isUpdating && handleDragStart(e, step.id, item.id)}
            onDragOver={handleDragOver}
            onDrop={(e) => !isUpdating && handleDrop(e, step.id, item.id)}
            onDragEnd={handleDragEnd}
            style={{
              margin: '8px 0',
              padding: '8px',
              backgroundColor:
                draggedItem?.columnId === step.id && draggedItem?.itemId === item.id
                  ? '#f0f0f0'
                  : '#F7F7F7',
              cursor: isUpdating ? 'not-allowed' : 'move',
              borderRadius: '5px',
              boxShadow: '0px 2px 0px 0px #00000029',
              opacity: isUpdating ? 0.6 : 1,
              pointerEvents: isUpdating ? 'none' : 'auto',
            }}
            onClick={(e) => {
              // Não permite clique se estiver atualizando
              if (isUpdating) return;
              
              // If click started on a "no-toggle" element, do nothing
              const target = e.target as HTMLElement;
              if (target.closest('[data-no-toggle="true"]')) return;

              const responsible = item.responsavel as ResponsibleCardModel;
              setResponsible({ id: responsible.id, name: responsible.aluno_nome } as ResponsibleModel);
              setCard(item);
              toggleCardResponsible(true);
            }}
          >
            <Stack flexDirection={'row'} justifyContent={'space-between'}>
              <Typography
                data-no-toggle="true"               // mark as no-toggle area
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  textDecoration: 'underline',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  width: '200px',
                  fontWeight: 500,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();              // prevent parent onClick
                  navigate('/termometro-geral-aluno', { state: { id: responsavel.aluno, name: responsavel.aluno_nome } });
                }}
              >
                {(item.responsavel as ResponsibleCardModel).aluno_nome}
              </Typography>

              {(ICON_STATUS[item.status] || isDone) && (
                <img width="28" height="28" src={isDone ? iconSuccess : ICON_STATUS[item.status]} alt="" />
              )}
            </Stack>
            <Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Box>
                <Typography fontSize={'12px'} color={'rgba(128, 128, 128, 1)'}>
                  Série: { serie.serie_nome }
                </Typography>
                <Typography fontSize={'12px'} color={'rgba(128, 128, 128, 1)'}>
                  Turma: { serie.turma.nome }
                </Typography>
                <Typography fontSize={'12px'} color={'rgba(128, 128, 128, 1)'}>
                  Turno: { serie.turno.nome }
                </Typography>
                <Typography fontSize={'12px'} color={'rgba(128, 128, 128, 1)'}>
                  Responsável: { responsavel.nome }
                </Typography>
              </Box>
              <ArrowForwardIos />
            </Stack>
            <Box
              mt={'15px'}
              sx={{
                background: 'rgba(208, 63, 60, 1)',
                borderRadius: '53px',
                p: '5px',
                maxWidth: '50px',
                textAlign: 'center',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {DateHelper.diffDays(item.created_at, new Date())}d
            </Box>
          </div>
        );
      })}
    </>
  );
};


export default ColumnCards;